import { Injectable } from '@angular/core';
import { Client } from '@df/core/service/client';
import { injectTimeoutService } from '@df/ng/timeout.service';
import last from 'lodash-es/last';
import sortBy from 'lodash-es/sortBy';
import take from 'lodash-es/take';
import { Subject } from 'rxjs/internal/Subject';
import { Subscription } from 'rxjs/internal/Subscription';
import { TrayComponent } from './tray.component';

class TrayInstance {
    private subscription: Subscription;

    constructor(
        readonly tray: TrayComponent,
        readonly trayService: TrayService
    ) {
        this.subscription = trayService.instancesChanges.subscribe(() => {
            tray.updatePosition();
        });
        trayService.instances.push(this);
        trayService.instances = sortBy(trayService.instances, item => -item.tray.priority);
        trayService.instancesChanges.next();
    }

    destroy() {
        this.subscription.unsubscribe();

        const index = this.trayService.instances.indexOf(this);
        if (index > -1) {
            this.trayService.instances.splice(index, 1);
            this.trayService.instancesChanges.next();
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class TrayService {
    protected subscriptions = new Subscription();
    /**
     * Update all trays positioning
     */
    updateAll: () => void;

    protected readonly timeoutService = injectTimeoutService();

    constructor(client: Client) {
        this.updateAll = this.timeoutService.throttle(() => {
            this.instances.forEach(item => item.tray.updatePosition());
        }, 100);
        this.subscriptions.add(client.obs.resize.subscribe(this.updateAll));
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    /////////////// REGISTRATION /////////////////

    /**
     * instances changes
     */
    readonly instancesChanges = new Subject<void>();

    /**
     * Trays collection
     */
    instances: TrayInstance[] = [];

    /**
     * Register tray
     */
    register(tray: TrayComponent) {
        // tslint:disable-next-line: no-unused-expression
        new TrayInstance(tray, this);
    }

    /**
     * Unregister tray
     */
    unregister(tray: TrayComponent) {
        this.getInstance(tray)?.destroy();
    }

    /////////////// CHANGE APPLICATION /////////////////

    /**
     * Applies shift to tray and adjusts all other trays in stack
     */
    updatePosition(tray: TrayComponent, position: number = 0) {
        this.getSameStackTrays(tray).forEach(item => item.tray.updatePosition(item.tray === tray ? position : 0));
    }

    /////////////// DISPLAY /////////////////

    /**
     * Retrieves z-index for given tray
     * Priority trays have higher z-index then later ones
     */
    getZIndex(tray: TrayComponent, base = 9): number {
        const instanceIndex = this.instances.findIndex(item => item.tray === tray);
        if (instanceIndex < 0) {
            return 0;
        }
        return base + (this.instances.length - instanceIndex); // the later in array the lower the priority
    }

    /**
     * Same stack (saem seqyence trays) appear one under another
     * with only the handle appearing
     *
     * So next tray is relativie to prev tray top position and not absolut bottom
     */
    getPriorityShift(tray: TrayComponent): number {
        return this.getFirstPriorityTrayBefore(tray)?.tray.heightUse || 0;
    }

    /////////////// FINDING TRAYS /////////////////

    /**
     * Retrieves tray instance that is exactly before this one (in terms of priority)
     */
    private getFirstPriorityTrayBefore(tray: TrayComponent): TrayInstance | undefined {
        return last(this.getPriorityTraysBefore(tray));
    }

    /**
     * Retrieves trays of the same stack
     * that have higher priority then given one
     */
    private getPriorityTraysBefore(tray: TrayComponent): TrayInstance[] {
        const instanceIndex = this.getInstanceIndex(tray);
        return instanceIndex > 0 ? take(this.instances, instanceIndex).filter(i => i.tray.sequence === tray.sequence) : [];
    }

    /**
     * Takes priorrity ordered tray instances for the same stack as requested one
     */
    private getSameStackTrays(tray: TrayComponent): TrayInstance[] {
        return this.instances.filter(item => item.tray.sequence === tray.sequence);
    }

    /**
     * Retrieves index of the tray in instances array
     */
    private getInstanceIndex(tray: TrayComponent): number {
        return this.instances.findIndex(i => i.tray === tray);
    }

    /**
     * Retrieves tray instance matched with tray (or undefined if not registered)
     */
    private getInstance(tray: TrayComponent): TrayInstance | undefined {
        return this.instances.find(i => i.tray === tray);
    }
}
